import { useState } from 'react'
import MFAccordion from '../../../components/ui-components/MFAccordion'
import AccordionHeaderContentWithTag from '../../../components/AccordionHeaderContentWithTag'
import { MUTUAL_FUND_DETAIL_IMAGE_URL } from '../../../constants'
import { EventNames } from '../../../types/misc.types'
import { trackEvent } from '../../../utils/appBridge'
import { getChangeDirection } from '../../../utils/mutualFundDetail'
import { ChangeDirection } from '../../../types/mutual-fund-detail.types'
import InvestmentBody from './investment/body/InvestmentBody'
import { InvestmentDataType } from '../types/investmentApi.types'
import { InvestmentAccordionDataModel } from '../types/investmentSection.types'

export default function InvestmentSectionAccordion({
  isin,
  investmentDataOverride,
  temporaryFamilyAccessToken,
  accordionDataModel
}: {
  isin: string
  investmentDataOverride: InvestmentDataType
  temporaryFamilyAccessToken: string
  accordionDataModel: InvestmentAccordionDataModel
}) {
  const [isExpanded, setIsExpanded] = useState(false)

  // Details_B05: one announcement — "Your Investment, ₹…, increase … XIRR, SIP …"
  const investmentTitle = `${accordionDataModel.titleLeadingText}${accordionDataModel.titleTrailingText}`
    .replace(/\u00A0/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
  const changeDirection = getChangeDirection(accordionDataModel.changeDirectionValue)
  const changePrefix =
    changeDirection === ChangeDirection.POSITIVE
      ? 'increase '
      : changeDirection === ChangeDirection.NEGATIVE
        ? 'decrease '
        : ''
  const accessibleName = [
    investmentTitle,
    accordionDataModel.value,
    accordionDataModel.changeText ? `${changePrefix}${accordionDataModel.changeText}` : null,
    accordionDataModel.showSipBadge ? accordionDataModel.sipText : null
  ]
    .filter(Boolean)
    .join(', ')

  return (
    <MFAccordion
      isExpanded={isExpanded}
      onAccordionClick={() =>
        setIsExpanded((isExpandedPrev) => {
          trackEvent({
            eventName: EventNames.UserClicked,
            payload: {
              eventId: 'My_Investment',
              responseName: isExpandedPrev ? 'Collapse' : 'Expand',
              screenType: 'MF_L2',
              fundName: isin,
              buttonLabel: accordionDataModel?.titleLeadingText ?? ''
            }
          })
          return !isExpandedPrev
        })
      }
      icon={`${MUTUAL_FUND_DETAIL_IMAGE_URL}/investment-icon.svg`}
      iconComponent={
        accordionDataModel.iconInitial ? (
          <div className='w-[43px] h-[43px] rounded-full bg-[#E6F2FF] flex items-center justify-center'>
            <div className='text-[19px] font-[500] text-[#1470CC] mb-[1px]'>{accordionDataModel.iconInitial}</div>
          </div>
        ) : null
      }
      title={accordionDataModel.title}
      accessibleName={accessibleName}
      middleContent={
        <div className='flex flex-col gap-2 w-full'>
          <AccordionHeaderContentWithTag
            title={accordionDataModel.title}
            titleComponent={
              <span
                id={`secondary-user-name-${accordionDataModel.titleLeadingText
                  .toLowerCase()
                  .replace(/[^a-z0-9]+/g, '-')
                  .replace(/^-+|-+$/g, '') || 'family'}`}
                className='flex items-center w-full min-w-0'
              >
                <span className='truncate min-w-0'>{accordionDataModel.titleLeadingText}</span>
                <span className='flex-shrink-0 whitespace-nowrap'>{accordionDataModel.titleTrailingText}</span>
              </span>
            }
            value={accordionDataModel.value}
            changeDirection={changeDirection}
            changeText={accordionDataModel.changeText}
          />
          {accordionDataModel.showSipBadge ? (
            <div className='h-[22px] text-[#6A6E6E] bg-[#F0F4F7] rounded-[8px] px-2 text-[12px] font-[500] flex items-center justify-center w-max'>
              {accordionDataModel.sipText}
            </div>
          ) : null}
        </div>
      }
      isBorderTop={true}
      headerAccordionBgColorClassName={accordionDataModel.headerAccordionBgColorClassName}
    >
      <InvestmentBody
        investmentDataOverride={investmentDataOverride}
        temporaryFamilyAccessToken={temporaryFamilyAccessToken}
      />
    </MFAccordion>
  )
}
